import React, { useEffect, useMemo, useState } from 'react';
import { useGetUserQuery } from '@services/authApi';
import { useTranslation } from 'react-i18next';
import { UserState } from '@features/Auth/user.slice';
import { useSelector } from 'react-redux';
import { RootState } from '@redux/store';
import { Locale } from 'types/locale';
import {
  COUNTRY_INFO,
  selectLanguageInfoToDisplay,
  toggleLanguage,
} from './utils/languageHelper';
import HeaderBar from './components/HeaderBar';

interface HeaderProps {
  isAlert: boolean;
}

const Header: React.FC<HeaderProps> = ({ isAlert }) => {
  const userState: UserState = useSelector((state: RootState) => state.user);

  const { i18n } = useTranslation();
  const { data: user } = useGetUserQuery(undefined, {
    skip: !userState || !userState.user,
  });

  const [locale, setLocale] = useState<Locale | null>(() => {
    const storedLocale = localStorage.getItem('locale');
    return storedLocale
      ? JSON.parse(storedLocale)
      : { country: 'en', language: 'en' };
  });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const openLanguageMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const closeLanguageMenu = () => {
    setAnchorEl(null);
  };

  const displayedLanguageInfo = useMemo(() => {
    if (!locale) return undefined;

    const currentLanguageIsoCode = locale.country;
    const userCountryIsoCode = user?.country?.isocode || locale.language;

    return selectLanguageInfoToDisplay(
      user,
      currentLanguageIsoCode,
      userCountryIsoCode
    );
  }, [locale, user]);

  const updateLocale = () => {
    if (!user?.country?.isocode || locale) return;

    const language = COUNTRY_INFO[user.country.isocode]?.language;

    const newLocale = {
      country: user.country.isocode,
      language: language,
    };

    localStorage.setItem('locale', JSON.stringify(newLocale));
    window.dispatchEvent(new Event('storage'));
    setLocale(newLocale);
  };

  useEffect(() => {
    updateLocale();
  }, [user, locale]);

  useEffect(() => {
    if (
      user?.signedIn &&
      locale.country !== user?.country?.isocode &&
      locale.country !== 'en'
    ) {
      toggleLanguage(user, locale, setLocale, closeLanguageMenu);
    }
  }, [user]);

  useEffect(() => {
    if (!locale) return;

    if (i18n.language !== locale.language) {
      i18n.changeLanguage(locale.language);
    }
  }, [locale]);

  return (
    <HeaderBar
      isAlert={isAlert}
      displayedLanguageInfo={displayedLanguageInfo}
      locale={locale}
      setLocale={setLocale}
      closeLanguageMenu={closeLanguageMenu}
      openLanguageMenu={openLanguageMenu}
      anchorEl={anchorEl}
    />
  );
};

export default Header;
